import { z } from 'zod';
import { BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN, VERLOFGROEP_KORT_VERZUIM, VERLOFSOORTEN, } from '../types/leave.js';
import { isEchteKalenderdatum } from '../lib/workabilityResolver.js';
/**
 * De toegestane gebeurtenissen voor het indien-eindpunt, AFGELEID uit de
 * gedeelde rechtentabel (AD-38, KE-9) — dezelfde vorm als
 * `VERLOFSOORT_WAARDEN` hieronder.
 */
const BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN_ZOD = BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN;
/**
 * De toegestane verlofsoort-waarden voor het indien-eindpunt, AFGELEID uit
 * `VERLOFSOORTEN` (AD-27, KE-9) in plaats van hier een tweede lijst te
 * onderhouden. Een soort erbij kost één regel in die tabel en niets hier.
 */
const VERLOFSOORT_WAARDEN = Object.keys(VERLOFSOORTEN);
/**
 * Leave (Verlof) zod schemas — Spoor 2 (Verlof MVP), Story 1.1 startpunt.
 *
 * `leaveRequestSchema` is intentionally the read/response shape (used to
 * validate/parse what the API returns). The create/submit input schema
 * (`createLeaveRequestSchema`, dagdeel-precisie, uren-voorrekening) is
 * extended in Story 1.3 on top of `requestStatusSchema`/`dayPartSchema`
 * defined here.
 */
export const requestStatusSchema = z.enum([
    'in_afwachting',
    'goedgekeurd',
    'afgewezen',
    'ingetrokken',
]);
export const dayPartSchema = z.enum(['hele_dag', 'ochtend', 'middag']);
/**
 * De waarden van de boek-as (AD-1, AD-28), AFGELEID uit het gedeelde type in
 * plaats van hier een tweede lijst te onderhouden (story 9.1, AC-8) —
 * dezelfde vorm als `VERLOFSOORT_WAARDEN` hierboven.
 *
 * Een TypeScript-union bestaat op runtime niet, dus de afleiding loopt langs
 * `Record<SyncStatus, true>`: die vorm dwingt de compiler af dat er geen
 * waarde ontbreekt én dat er geen waarde te veel staat. Komt er ooit een
 * vijfde waarde bij `SyncStatus`, dan breekt de build hier — en niet pas op
 * de eerste respons die door `leaveRequestRecordSchema` gaat.
 */
const SYNC_STATUS_WAARDEN = Object.keys({
    pending: true,
    synced: true,
    failed: true,
    niet_boekend: true,
});
export const syncStatusSchema = z.enum(SYNC_STATUS_WAARDEN);
// Named `leaveRequestRecordSchema` (not `leaveRequestSchema`) to avoid
// colliding with the existing `leaveRequestSchema` in `./nmbrs.js` — that
// one is the legacy direct-insert-flow submit-input schema (AD-1/AD-2: the
// route it backs is replaced by the new workflow endpoints in Story 1.3+;
// it is not removed here to avoid an unrelated blast radius in Story 1.1).
// This schema is the new workflow read/record shape.
export const leaveRequestRecordSchema = z
    .object({
    id: z.number().int(),
    employeeId: z.number().int(),
    leaveType: z.number().int(),
    startDate: z.string(),
    endDate: z.string(),
    startPart: dayPartSchema,
    endPart: dayPartSchema,
    hours: z.number(),
    // Story 1.3 (AD-11): only ever non-null when startDate === endDate AND
    // startPart/endPart === 'hele_dag' — see createLeaveRequestSchema below.
    startTime: z.string().optional().nullable(),
    endTime: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
    requestStatus: requestStatusSchema,
    decidedBy: z.number().int().optional().nullable(),
    decidedAt: z.string().optional().nullable(),
    decisionReason: z.string().optional().nullable(),
    syncStatus: syncStatusSchema.optional(),
})
    .refine((data) => {
    // Afwijzing vereist een reden (AD-1: "decision_reason, verplicht bij afwijzing").
    if (data.requestStatus === 'afgewezen' && !data.decisionReason)
        return false;
    return true;
}, { message: 'Afwijzing vereist een reden', path: ['decisionReason'] });
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
const timeOfDayPattern = /^([01]\d|2[0-3]):[0-5]\d$/;
/**
 * Marker on a zod issue raised by the `startTime`/`endTime` vormfout-refine
 * below (Story 1.3, AC-16). The route (`routes/leaveRequests.ts`) inspects
 * this to answer with `LEAVE_TIME_INVALID` instead of the generic
 * `VALIDATION_ERROR` — the AC is explicit about the wire-level error code,
 * and that code has to survive a zod-level rejection too ("Zod weigert
 * dezelfde drie gevallen al vóór de service").
 */
export const LEAVE_TIME_INVALID_ZOD_CODE = 'LEAVE_TIME_INVALID';
/** Story 1.3: POST /api/v1/leave-requests input. */
export const createLeaveRequestSchema = z
    .object({
    leaveType: z.number().int().min(1).max(4),
    startDate: z.string().regex(isoDatePattern, 'Ongeldige datum (YYYY-MM-DD)'),
    endDate: z.string().regex(isoDatePattern, 'Ongeldige datum (YYYY-MM-DD)'),
    startPart: dayPartSchema,
    endPart: dayPartSchema,
    // Geen bovengrens hier: dit veld bestaat ook op het zelf-aanvraagpad, waar
    // een leidinggevende de waarde nog beoordeelt. Op het namens-pad is er geen
    // tweede paar ogen — daar leunt de bescherming op de bevestigingsstap in de
    // UI. Een harde bovengrens is als open punt gemeld bij Friso.
    hours: z.number().positive().optional(), // manual override (AC 1: "overschrijfbaar")
    description: z.string().max(2000).optional(),
    // Story 1.3 (AD-11, AC-12/AC-16): alleen geldig bij een aanvraag binnen
    // één dag met dagdeel 'hele_dag' — de drie vormregels worden hieronder in
    // `.superRefine` afgedwongen, niet hier: de vorm alleen (HH:MM) zegt niks
    // over of het veld hier ÜBERHAUPT mag staan.
    startTime: z.string().regex(timeOfDayPattern, 'Ongeldige tijd (HH:MM)').optional(),
    endTime: z.string().regex(timeOfDayPattern, 'Ongeldige tijd (HH:MM)').optional(),
    // "Namens een medewerker aanvragen"-override (AD-2: vervangt de legacy
    // direct-insert route). De aanwezigheid van dit veld is NOOIT op zichzelf
    // autorisatie — de route toetst het apart.
    //
    // ⚠ GEWIJZIGD 2026-07-29: was hr/admin-only. De route toetst nu
    // `assertNotSelfApproval` + `assertTeamManagerOrHR`, dus ook een
    // leidinggevende mag dit voor de eigen teamleden. Een aanvraag met dit
    // veld wordt direct goedgekeurd en meteen in Nmbrs geboekt, zonder tweede
    // beoordeling — behandel elke wijziging hieraan als een wijziging aan de
    // loonadministratie.
    employeeId: z.number().int().positive().optional(),
    // Story 7.5: de gekozen verlofsoort. Zonder dit veld bereikt de keuze uit
    // het scherm de dienstlaag niet en blijft `hr_stap_reden` altijd leeg —
    // dan is de hele HR-stap met één ontbrekende zod-regel uitgeschakeld.
    // De waarden komen uit `VERLOFSOORTEN` (AD-27) en staan hier niet met de
    // hand uitgeschreven.
    verlofsoort: z.enum(VERLOFSOORT_WAARDEN).optional(),
    // Story 10.1, taak 3: de gekozen gebeurtenis bij bijzonder verlof. De
    // waarden komen uit `BIJZONDER_VERLOF_GEBEURTENISSEN` (AD-38) en staan
    // hier niet met de hand uitgeschreven — één rechtentabel, ook voor deze
    // laag. Wat er niet in staat is geen bijzonder verlof; de dienst weigert
    // hetzelfde geval nog een keer met `VALIDATION_ERROR` (AD-2, KE-5).
    bijzondereGebeurtenis: z.enum(BIJZONDER_VERLOF_GEBEURTENIS_WAARDEN_ZOD).optional(),
    // Story 10.4, taak 2: het vinkje *de datum van de begrafenis of crematie
    // staat nog niet vast*. Een EIGEN vlag en geen afleiding uit "de
    // gebeurtenis is een overlijden" (AD-26, FR-20): de medewerker vinkt hem
    // aan of niet, en die handeling moet terug te vinden zijn.
    einddatumVoorlopig: z.boolean().optional(),
})
    .refine((data) => data.startDate <= data.endDate, {
    message: 'Einddatum moet op of na de startdatum liggen',
    path: ['endDate'],
})
    .superRefine((data, ctx) => {
    const hasStart = data.startTime !== undefined;
    const hasEnd = data.endTime !== undefined;
    if (!hasStart && !hasEnd)
        return; // geen tijden ingevuld — niets te toetsen (AC-1/AC-2)
    // Regel 1: beide samen aanwezig of samen afwezig (AC-16).
    if (hasStart !== hasEnd) {
        const emptyPath = hasStart ? 'endTime' : 'startTime';
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Vul een begin- en eindtijd in.',
            path: [emptyPath],
            params: { leaveTimeInvalid: LEAVE_TIME_INVALID_ZOD_CODE },
        });
        return; // de twee regels hieronder gaan uit van een volledig paar
    }
    // Regel 2: alleen bij een aanvraag binnen één dag (AD-11).
    if (data.startDate !== data.endDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Tijden zijn alleen mogelijk bij een aanvraag van één dag.',
            path: ['startTime'],
            params: { leaveTimeInvalid: LEAVE_TIME_INVALID_ZOD_CODE },
        });
    }
    // Regel 3: alleen bij dagdeel 'hele_dag' — een dagdeel-boeking is al een
    // tijdvak (AD-11). Beide kanten getoetst: het scherm zet ze bij een
    // aanvraag binnen één dag altijd gelijk, maar de server vertrouwt dat niet.
    if (data.startPart !== 'hele_dag' || data.endPart !== 'hele_dag') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Tijden zijn alleen mogelijk bij dagdeel 'hele dag'.",
            path: ['startTime'],
            params: { leaveTimeInvalid: LEAVE_TIME_INVALID_ZOD_CODE },
        });
    }
})
    // ── Story 5.3, AC-10: de spiegelhelft die WÉL datumonafhankelijk is ──
    //
    // De VERPLICHTING om een soort mee te sturen hoort hier terecht niet: die
    // hangt aan de ingangsdatum, en dit schema kent die datum niet en mag hem
    // niet opzoeken (AD-15, AD-27). De andere helft van AC-10 hangt er níet
    // aan — een soort meesturen op verlofgroep 1, 2 of 4 is verkeerd op élke
    // dag, ook ná de ingangsdatum, want daar gaan de verlofsoorten nooit
    // gelden (FR-1).
    //
    // De dienst weigert hetzelfde geval met
    // `LEAVE_SOORT_NIET_OP_DEZE_VERLOFGROEP`; dit is de eerste laag, niet de
    // vervanging ervan (AD-2). Zonder deze regel hangt die hele poort aan één
    // regel in één functie.
    //
    // Gevonden bij de code review van 2026-08-17.
    .superRefine((data, ctx) => {
    if (data.verlofsoort !== undefined && data.leaveType !== VERLOFGROEP_KORT_VERZUIM) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Een verlofsoort hoort alleen bij kort verzuim en bijzonder verlof.',
            path: ['verlofsoort'],
        });
    }
});
/**
 * Story 3.2: POST /api/v1/leave-requests/:id/decide input.
 * AC 2: `reason` is required when `decision === 'reject'` (validated here,
 * before the service is ever called — the UI-layer disabled-button is a
 * second, non-authoritative defense).
 */
export const decideLeaveRequestSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    // Trimmed server-side too (not just by the RejectDialog UI) so a
    // whitespace-only or padded reason never ends up stored verbatim, even
    // for a direct API caller bypassing the UI.
    reason: z
        .string()
        .max(2000)
        .transform((v) => v.trim())
        .optional(),
})
    .refine((data) => data.decision !== 'reject' || (data.reason !== undefined && data.reason.length > 0), {
    message: 'Reden is verplicht bij afwijzen',
    path: ['reason'],
});
/**
 * Marker on a zod issue raised by the `reason`-leeg-refine below (Story 2.4,
 * AC-3). Mirrors `LEAVE_TIME_INVALID_ZOD_CODE`'s pattern: the route
 * (`routes/leaveRequests.ts`) inspects this to answer with
 * `CORRECTION_REASON_REQUIRED` instead of the generic `VALIDATION_ERROR`.
 */
export const CORRECTION_REASON_REQUIRED_ZOD_CODE = 'CORRECTION_REASON_REQUIRED';
/**
 * Story 2.4: POST /api/v1/leave-requests/:id/corrections input.
 * AD-11: hetzelfde urenbegrip als het origineel — dus óf `newHours`
 * (dagdeel-gebaseerd), óf `newStartTime`+`newEndTime` (tijden-gebaseerd),
 * nooit beide en nooit geen van beide. AC-3: `reason` is ALTIJD verplicht
 * (in tegenstelling tot `decideLeaveRequestSchema`, waar het alleen bij
 * afwijzen verplicht is) — ook getrimd, zodat een alleen-witruimte waarde
 * hier al wordt geweigerd, vóór de service.
 */
export const submitLeaveCorrectionSchema = z
    .object({
    // Story 2.7 (AC-2, UX-DR9): `0` is een geldige uitkomst — "verlof laten
    // vervallen" is geen apart pad maar de natuurlijke uitkomst van nul
    // invullen. `.min(0)` in plaats van `.positive()`: negatief blijft
    // geweigerd, nul niet meer.
    newHours: z.number().min(0).optional(),
    newStartTime: z.string().regex(timeOfDayPattern, 'Ongeldige tijd (HH:MM)').optional(),
    newEndTime: z.string().regex(timeOfDayPattern, 'Ongeldige tijd (HH:MM)').optional(),
    reason: z
        .string()
        .max(500)
        .transform((v) => v.trim()),
})
    .superRefine((data, ctx) => {
    if (data.reason.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Vul kort in waarom je dit aanpast.',
            path: ['reason'],
            params: { correctionReasonRequired: CORRECTION_REASON_REQUIRED_ZOD_CODE },
        });
    }
    const hasStart = data.newStartTime !== undefined;
    const hasEnd = data.newEndTime !== undefined;
    if (hasStart !== hasEnd) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Vul een begin- en eindtijd in.',
            path: [hasStart ? 'newEndTime' : 'newStartTime'],
            params: { leaveTimeInvalid: LEAVE_TIME_INVALID_ZOD_CODE },
        });
        return; // de twee regels hieronder gaan uit van een volledig paar (of geen van beide)
    }
    const usesTijden = hasStart && hasEnd;
    if (!usesTijden && data.newHours === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Vul het aantal uren in, of een van-tot tijdvak.',
            path: ['newHours'],
            params: { leaveTimeInvalid: LEAVE_TIME_INVALID_ZOD_CODE },
        });
    }
    if (usesTijden && data.newHours !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Vul óf tijden óf uren in, niet allebei.',
            path: ['newHours'],
            params: { leaveTimeInvalid: LEAVE_TIME_INVALID_ZOD_CODE },
        });
    }
});
/**
 * Story 2.6: POST /api/v1/leave-requests/corrections/:id/decide input.
 *
 * Zelfde vorm als `decideLeaveRequestSchema` — een reden is alleen verplicht
 * bij afwijzen (AC-7), en wordt server-side getrimd zodat een alleen-witruimte
 * reden nooit opgeslagen wordt, ook niet door een directe API-aanroep die het
 * scherm overslaat.
 */
export const decideLeaveCorrectionSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    reason: z
        .string()
        .max(2000)
        .transform((v) => v.trim())
        .optional(),
})
    .refine((data) => data.decision !== 'reject' || (data.reason !== undefined && data.reason.length > 0), {
    message: 'Reden is verplicht bij afwijzen',
    path: ['reason'],
});
/**
 * Story 7.8 — de enige invoer die HR bij een periode-aanpassing meestuurt: de
 * nieuwe einddatum (FR-31).
 *
 * De begindatum staat er met opzet NIET in. Verlof naar een andere datum
 * verplaatsen is buiten scope, en een veld dat de client wél mag meesturen is
 * vroeg of laat een veld dat iemand meestuurt. Dat de nieuwe einddatum niet
 * vóór de begindatum ligt, kan hier niet getoetst worden — die datum staat op
 * de rij en niet in de payload; de dienst weigert dat geval.
 */
export const updateLeaveEndDateSchema = z.object({
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (verwacht JJJJ-MM-DD)'),
});
/**
 * Story 5.5 — de enige invoer die HR bij een soortcorrectie meestuurt: de
 * nieuwe soort (FR-2, AC-1).
 *
 * De waarden komen uit `VERLOFSOORT_WAARDEN`, afgeleid uit `VERLOFSOORTEN`
 * (AD-27) — nooit met de hand overgeschreven. Anders dan bij het indienen is
 * het veld hier VERPLICHT: een correctie zonder soort is geen handeling.
 */
export const updateVerlofsoortSchema = z.object({
    verlofsoort: z.enum(VERLOFSOORT_WAARDEN),
});
/**
 * Story 6.2 — de queryparameters van `GET /leave-requests/maandruimte`.
 *
 * `datum` is VERPLICHT: die bepaalt over welke kalendermaand de stand gaat,
 * en zonder hem is er geen vraag om te beantwoorden. `uren` is optioneel — de
 * uren van de aanvraag in wording, waarmee de SERVER kan vaststellen of deze
 * aanvraag boven de grens uitkomt (KE-5). Zonder dat getal levert het eindpunt
 * gewoon de resterende ruimte en niets over een overschrijding.
 *
 * `employeeId` is optioneel en betekent *namens deze medewerker*. Afwezig is
 * altijd de eigen stand; aanwezig loopt langs dezelfde poort als het
 * schrijfpad, dus het veld verruimt niets.
 *
 * Alle drie komen als tekst binnen — het is een querystring — en worden hier
 * omgezet. Een niet-numerieke `uren` of `employeeId` is een vormfout en geen
 * stilzwijgende nul.
 */
export const maandruimteQuerySchema = z.object({
    // De VORM is niet genoeg (code review 2026-08-17): `2026-13-05` past er
    // precies in, en het eindpunt valt daarna terug op de rauwe waarde. Dat
    // levert een 200 met een maand die niet bestaat — *"Je hebt in 2026-13 nog
    // 3 uur van je 3 uur over."* Een dag die niet op de kalender staat is een
    // vormfout en hoort hier te stranden, niet op het scherm.
    datum: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (verwacht JJJJ-MM-DD)')
        .refine(isEchteKalenderdatum, 'Ongeldige datum (verwacht JJJJ-MM-DD)'),
    uren: z.coerce.number().positive().optional(),
    employeeId: z.coerce.number().int().positive().optional(),
});
//# sourceMappingURL=leave.js.map